import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, PhoneIcon } from "lucide-react";
import { ButtonLink } from "../shared/Button";
import { iconMap } from "../shared/iconMap";
import { RooflineDivider } from "../shared/RooflineDivider";

// [Branding] [Conversion] [Journey] Centred, symmetrical full-bleed hero + "What's your next move?" selector.
// Persona: established homeowners (upgraders, downsizers, retirees, rebuilders and investors).
// Image is ILLUSTRATIVE (generated): replace with approved Oceanside photography or the Padbury video poster.
const HERO_IMAGE = "https://cdn.magicpatterns.com/uploads/cNLE7H3ZzdDxXv69oE1iQh/Ocean.avif";
const EASE: [number, number, number, number] = [0.23, 1, 0.32, 1];
const nextMoves = [{
  title: "Explore single-storey designs",
  detail: "Six designs, 10m to 15m lots",
  link: "/home-designs",
  icon: "Home"
}, {
  title: "Rebuild on my block",
  detail: "Stay in the street you love",
  link: "/#knock-down-rebuild",
  icon: "RefreshCw"
}, {
  title: "Build on my rear block",
  detail: "Make the most of your land",
  link: "/#rear-block-homes",
  icon: "MapPinned"
}, {
  title: "Design a custom home",
  detail: "Built around your brief",
  link: "/#custom-homes",
  icon: "PencilRuler"
}, {
  title: "Invest or develop",
  detail: "Duplexes & multi-unit",
  link: "/#developments",
  icon: "Building2"
}];
type HeroProps = {
  emphasis?: "consultation-first" | "design-first";
  headline?: "as-our-own" | "owners-care" | "checked-every-stage";
};

// Tagline options (client feedback: avoid "our own standard", which could be read as subjective or below code).
const taglines: Record<NonNullable<HeroProps["headline"]>, string> = {
  "as-our-own": "Built as if it were our own.",
  "owners-care": "Built with an owner’s care.",
  "checked-every-stage": "Quality checked at every stage."
};
export function Hero({
  emphasis = "consultation-first",
  headline = "as-our-own"
}: HeroProps) {
  const reduce = useReducedMotion();
  const consultFirst = emphasis === "consultation-first";
  const enter = (delay: number) => ({
    initial: reduce ? false : {
      opacity: 0,
      y: 14
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.28,
      ease: EASE,
      delay
    }
  });
  return <section aria-labelledby="hero-heading" className="bg-paper" data-id="element-235" data-mp="2vup4lf1ourk2">
      <div className="relative isolate overflow-hidden bg-charcoal" data-id="element-236" data-mp="2vt4crx6rdmpw">
        <img src={HERO_IMAGE} alt="" width={1376} height={768} loading="eager" className="absolute inset-0 -z-10 h-full w-full object-cover object-center" data-id="element-237" data-mp="2htutupr64ei8" />
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-charcoal/75" data-id="element-238" data-mp="27zcqjlpnykyy" />

        <div className="mx-auto max-w-site px-5 pb-36 pt-16 text-center md:px-10 md:pb-44 md:pt-24 lg:pb-48 lg:pt-28" data-id="element-239" data-mp="2cwl93neq4r0q">
          <motion.p {...enter(0)} className="inline-flex items-center gap-3 rounded-full border border-paper/25 px-4 py-1.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-paper/85" data-id="element-240" data-mp="642b2zf6otum">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-ocean" data-id="element-241" data-mp="1vkxqyhgq2lg0" />
            Owner-led home builders · Perth
          </motion.p>
          <motion.h1 {...enter(0.05)} id="hero-heading" className="mx-auto mt-6 max-w-5xl text-[42px] font-semibold leading-[1.02] tracking-[-0.03em] text-paper md:text-[68px] xl:text-[80px]" data-id="element-242" data-mp="25d3oe6pqtv2l">
            Your next home in Perth.
            <span className="block text-ocean" data-id="element-243" data-mp="2ac6odcqv9b3p">{taglines[headline]}</span>
          </motion.h1>
          <motion.p {...enter(0.1)} className="mx-auto mt-6 max-w-2xl text-[18px] leading-relaxed text-paper/85 md:text-[20px]" data-id="element-244" data-mp="2obftnhdjaksw">
            Moving up, downsizing or rebuilding on the block you love? An owner-led Perth builder with 30+ years’ combined WA
            experience, fixed-price contracts and a consistent point of contact from first meeting to handover.
          </motion.p>
          <motion.div {...enter(0.15)} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-245" data-mp="qfr9lkrgpzt9">
            <ButtonLink to={consultFirst ? "/contact" : "/home-designs"} data-id="element-246" data-mp="veav5k2599l3">
              {consultFirst ? "Book a consultation" : "Explore home designs"}
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-247" data-mp="10eor43qh0t2r" />
            </ButtonLink>
            <ButtonLink to={consultFirst ? "/home-designs" : "/contact"} variant="inverse" data-id="element-248" data-mp="15c8i8489p0c4">
              {consultFirst ? "Explore home designs" : "Book a consultation"}
            </ButtonLink>
          </motion.div>
          <motion.p {...enter(0.2)} className="mt-6 text-[15px] text-paper/75" data-id="element-249" data-mp="clzxc4mck1tu">
            Prefer to talk?{" "}
            <a href="tel:+61862850876" className="inline-flex items-center gap-1.5 font-semibold text-paper underline underline-offset-4" data-id="element-250" data-mp="me5oxd8osh8i">
              <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-251" data-mp="3l3362s0unr9m" /> 08 6285 0876
            </a>
          </motion.p>
        </div>
      </div>

      <nav aria-labelledby="next-move-heading" className="relative z-10 mx-auto -mt-24 max-w-site px-5 md:-mt-28 md:px-10" data-id="element-252" data-mp="3q0w8d9z1814m">
        <div className="rounded-[28px] border border-line bg-white p-5 shadow-[0_30px_60px_-30px_rgba(43,39,39,0.35)] md:p-8" data-id="element-253" data-mp="236sqs9xecws5">
          <h2 id="next-move-heading" className="text-center font-heading text-[20px] font-semibold tracking-tight md:text-[24px]" data-id="element-254" data-mp="1y6de7kkxvtb8">
            What’s your next move?
          </h2>
          <ul className="mt-5 grid grid-cols-2 gap-3 md:mt-6 lg:grid-cols-5" data-id="element-255" data-mp="1o9s96z8x6i7e">
            {nextMoves.map((move, index) => {
            const Icon = iconMap[move.icon];
            return <li key={move.title} className={index === nextMoves.length - 1 ? "col-span-2 lg:col-span-1" : ""} data-id="element-256" data-mp="1fgf6j5l20284" data-mpi={index}>
                  <Link to={move.link} className="group flex h-full flex-col items-center gap-3 rounded-2xl border border-line bg-paper px-3 py-5 text-center transition-[transform,border-color,background-color,box-shadow] duration-200 ease-out hover:-translate-y-1 hover:border-ocean hover:bg-white hover:shadow-[0_14px_28px_-18px_rgba(43,39,39,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean" data-id="element-257" data-mp="1aiwsx1w3cyts">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-charcoal ring-1 ring-line transition-colors duration-200 ease-out group-hover:bg-ocean group-hover:ring-ocean" data-id="element-258" data-mp="hw5i5wo1eqh4">
                      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-259" data-mp="19nuurean2kd1" />
                    </span>
                    <span data-id="element-260" data-mp="1eklc04y6jm01">
                      <span className="block font-heading text-[15px] font-semibold leading-tight text-charcoal md:text-[16px]" data-id="element-261" data-mp="1ekuyhik0nb0z">{move.title}</span>
                      <span className="mt-1 block text-[13px] text-slate md:text-[14px]" data-id="element-262" data-mp="14vbeuaohbd52">{move.detail}</span>
                    </span>
                  </Link>
                </li>;
          })}
          </ul>
        </div>
      </nav>
      <div className="mx-auto max-w-site px-5 md:px-10" data-id="element-263" data-mp="1xdfdbw2vrfls">
        <RooflineDivider className="mt-14 text-line" data-id="element-264" data-mp="3i1rl2woi349r" />
      </div>
    </section>;
}